import React, { useEffect, useState } from 'react';
import { Sparkles, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_DAYS = 60;

// Module-level cache — the "new customer" window is a single admin setting,
// so every table/drawer shares one fetch instead of one request per row.
let cachedDays = null;
let inflight = null;

export function resetNewCustomerDaysCache() {
  cachedDays = null;
  inflight = null;
}

export function useNewCustomerDays(token) {
  const [days, setDays] = useState(cachedDays ?? FALLBACK_DAYS);
  useEffect(() => {
    if (cachedDays !== null || !token) return;
    if (!inflight) {
      inflight = fetch(`${API_URL}/api/config/customer-badges`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => { cachedDays = Number(j?.new_customer_days) || FALLBACK_DAYS; return cachedDays; })
        .catch(() => { cachedDays = FALLBACK_DAYS; return cachedDays; });
    }
    let alive = true;
    inflight.then((v) => { if (alive) setDays(v); });
    return () => { alive = false; };
  }, [token]);
  return days;
}

export function daysSince(dateLike) {
  if (!dateLike) return null;
  const t = new Date(dateLike).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

/**
 * Two icon-only admin markers shown next to a customer name:
 *   • emerald sparkle  — registered less than `newDays` days ago
 *   • blue building    — registered as a company account
 * Icon-only by design so it stays readable in dense tables and on mobile;
 * the details live in the native tooltip.
 */
export const CustomerBadges = ({
  isCompany = false,
  companyName = null,
  registrationDate = null,
  newDays = FALLBACK_DAYS,
  size = 'sm',
  className = '',
  testid = 'customer-badges',
}) => {
  const age = daysSince(registrationDate);
  const isNew = age !== null && age < Number(newDays || FALLBACK_DAYS);
  if (!isNew && !isCompany) return null;

  const dot  = size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  const glyph = size === 'md' ? 'w-3 h-3' : 'w-2.5 h-2.5';

  return (
    <span className={`inline-flex items-center gap-1 align-middle flex-shrink-0 ${className}`} data-testid={testid}>
      {isNew && (
        <span
          title={`New customer · registered ${age} day${age === 1 ? '' : 's'} ago (window: ${newDays} days)`}
          data-testid={`${testid}-new`}
          className={`${dot} rounded-full bg-emerald-500 text-white inline-flex items-center justify-center ring-1 ring-emerald-300/70 shadow-sm`}
        >
          <Sparkles className={glyph} />
        </span>
      )}
      {isCompany && (
        <span
          title={`Company account${companyName ? ` · ${companyName}` : ''}`}
          data-testid={`${testid}-company`}
          className={`${dot} rounded-full bg-blue-600 text-white inline-flex items-center justify-center ring-1 ring-blue-300/70 shadow-sm`}
        >
          <Building2 className={glyph} />
        </span>
      )}
    </span>
  );
};

export default CustomerBadges;
